import { useEffect, useState } from "react";
import { startMeetingNow } from "@vincer/services/api/dyte";
import { meetingApi } from "@vincer/services/api/meetingApi";
import { userApi } from "@vincer/services/api/userApi";
import { Assignment, Meeting } from "@vincer/types";
import { format } from "date-fns";
import {
  Calendar,
  CalendarPlus,
  Clock,
  DollarSign,
  Users,
  Video,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { useUser } from "../../../../hooks/useUser";
import { getMentorAssignments } from "../../data/mentorAssignments";

export function Dashboard() {
  const navigate = useNavigate();
  const { data: user } = useUser();

  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [meetings, setMeetings] = useState<Meeting[]>([]);

  useEffect(() => {
    if (user) {
      getMentorAssignments(user.uuid).then((assignments) => {
        setAssignments(assignments.data || []);
      });
      meetingApi.getMeetings(user.uuid, "mentor").then((meetings) => {
        setMeetings(meetings);
      });
    }
  }, [user]);

  // const upcomingSessions = mockSessions.filter(
  //   (session) => session.status === "upcoming"
  // );
  // const completedSessions = mockSessions.filter(
  //   (session) => session.status === "completed"
  // );

  const startSession = async (assignment: Assignment) => {
    const mentorRecord = await userApi.getMentorRecord(user?.uuid || "");
    if (!mentorRecord) {
      console.error("Mentor record not found");
      return;
    }
    const meeting = await startMeetingNow(mentorRecord, assignment.mentee);

    // TODO: Hide the auth token from the URL
    navigate(
      `/mentor/video-call?sessionId=${meeting.uuid}&authToken=${meeting.mentor_auth_token}`
    );
  };

  return (
    <div className="mx-auto max-w-7xl space-y-8">
      {/* Quick Actions Header */}
      <div className="flex items-center justify-between">
        <div className="flex gap-4">
          <Button
            className="bg-gradient-primary flex items-center gap-2 border-0 shadow-lg hover:opacity-90"
            onClick={() => {
              navigate("/mentor/calendar/availability");
            }}
          >
            <CalendarPlus className="size-4" />
            Sync Calendar
          </Button>
          <Button
            variant="outline"
            className="border-primary/20 hover:bg-primary/5"
          >
            Schedule Session
          </Button>
        </div>
        <div className="text-right">
          <p className="text-sm text-muted-foreground">Today's Date</p>
          <p className="text-sm font-medium text-foreground">
            {format(new Date(), "EEEE, MMMM d, yyyy")}
          </p>
        </div>
      </div>

      {/* Stats Cards - Now 4 columns */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
        <Card className="bg-gradient-card border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Mentees</p>
                <p className="text-2xl font-semibold text-primary">
                  {assignments.length}
                </p>
              </div>
              <div className="bg-gradient-primary flex size-12 items-center justify-center rounded-full">
                <Users className="size-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-card border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  Upcoming Sessions
                </p>
                <p className="text-2xl font-semibold text-primary">
                  {meetings.filter((mtg) => mtg.status === "confirmed").length}
                </p>
              </div>
              <div className="bg-gradient-success flex size-12 items-center justify-center rounded-full">
                <Calendar className="size-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-card border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Dollars Earned</p>
                <p className="text-2xl font-semibold text-primary">$1,045</p>
              </div>
              <div
                className="flex size-12 items-center justify-center rounded-full"
                style={{
                  background:
                    "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                }}
              >
                <DollarSign className="size-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-card border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  Hours This Month
                </p>
                <p className="text-2xl font-semibold text-primary">
                  {meetings
                    .filter((mtg) => {
                      return (
                        new Date(mtg.scheduled_time).getMonth() ===
                          new Date().getMonth() && mtg.status === "ended"
                      );
                    })
                    .reduce((sum, current) => sum + current.duration / 60, 0)}
                </p>
              </div>
              <div className="bg-gradient-secondary flex size-12 items-center justify-center rounded-full">
                <Clock className="size-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* My Mentees Section */}
        <Card className="bg-gradient-card border-0 shadow-xl">
          <CardHeader className="border-b border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10">
            <CardTitle className="flex items-center gap-2 text-primary">
              <Users className="size-5" />
              My Mentees ({assignments.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 p-6">
            {assignments.map((assignment) => (
              <div
                key={assignment.uuid}
                className="flex items-center justify-between rounded-xl border border-primary/10 bg-white/60 p-4 backdrop-blur-sm transition-all duration-200 hover:shadow-md"
              >
                <div className="flex items-center gap-3">
                  <Avatar className="ring-2 ring-primary/20">
                    <AvatarImage
                      src={assignment.mentee.avatar || ""}
                      alt={assignment.mentee.name}
                    />
                    <AvatarFallback className="bg-gradient-primary text-white">
                      {assignment.mentee.name.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-medium text-foreground">
                      {assignment.mentee.name}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {assignment.mentee.jobTitle} at{" "}
                      {assignment.mentee.company}
                    </p>
                    {/* {assignment.mentee.nextSession && (
                      <p className="mt-1 text-sm font-medium text-primary">
                        Next: {mentee.nextSession}
                      </p>
                    )} */}
                  </div>
                </div>
                <Button
                  onClick={() => {
                    startSession(assignment);
                  }}
                  className="flex items-center gap-2 border-0 text-white shadow-lg hover:opacity-80"
                  style={{
                    background:
                      "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  }}
                  disabled={false}
                >
                  <Video className="size-4" />
                  Start Session
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Booked Sessions Section */}
        <Card className="bg-gradient-card border-0 shadow-xl">
          <CardHeader className="border-b border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10">
            <CardTitle className="flex items-center gap-2 text-primary">
              <Calendar className="size-5" />
              Booked Sessions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 p-6">
            <div>
              <h4 className="mb-3 font-medium text-foreground">
                Upcoming Sessions (
                {meetings.filter((mtg) => mtg.status === "confirmed").length})
              </h4>
              <div className="space-y-3">
                {meetings
                  .filter((mtg) => mtg.status === "confirmed")
                  .map((session) => (
                    <div
                      key={session.uuid}
                      className="flex items-center justify-between rounded-xl border border-primary/10 bg-gradient-to-r from-blue-50 to-indigo-50 p-4"
                    >
                      <div>
                        <p className="font-medium text-foreground">
                          {session.mentee?.vincer_base_user.name}
                        </p>
                        {
                          <p className="text-sm text-muted-foreground">
                            Follow up Session
                          </p>
                        }
                        <div className="mt-2 flex items-center gap-4">
                          <span className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Calendar className="size-3" />
                            {format(
                              new Date(session.scheduled_time),
                              "MMM d, yyyy"
                            )}
                          </span>
                          <span className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Clock className="size-3" />
                            {format(
                              new Date(session.scheduled_time),
                              "h:mm a"
                            )}{" "}
                            ({session.duration} min)
                          </span>
                        </div>
                      </div>
                      <Badge className="bg-gradient-primary border-0 text-white">
                        {session.status}
                      </Badge>
                    </div>
                  ))}
              </div>
            </div>

            <div className="border-t border-primary/10 pt-4">
              <h4 className="mb-3 font-medium text-foreground">
                Recent Sessions (
                {meetings.filter((mtg) => mtg.status === "ended").length})
              </h4>
              <div className="space-y-3">
                {meetings
                  .filter((mtg) => mtg.status === "confirmed")
                  .map((session) => (
                    <div
                      key={session.uuid}
                      className="flex items-center justify-between rounded-xl border border-gray-200 bg-white/40 p-4 backdrop-blur-sm"
                    >
                      <div>
                        <p className="font-medium text-foreground">
                          {session.mentee?.vincer_base_user.name}
                        </p>
                        {/* <p className="text-sm text-muted-foreground">
                          {session.status}
                        </p> */}
                        <div className="mt-2 flex items-center gap-4">
                          <span className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Calendar className="size-3" />
                            {format(
                              new Date(session.scheduled_time),
                              "MMM d, yyyy"
                            )}
                          </span>
                          <span className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Clock className="size-3" />
                            {format(
                              new Date(session.scheduled_time),
                              "h:mm a"
                            )}{" "}
                            ({session.duration} min)
                          </span>
                        </div>
                      </div>
                      <Badge
                        variant="outline"
                        className="border-green-200 text-green-700"
                      >
                        {session.status}
                      </Badge>
                    </div>
                  ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
